import { ComponentType, PropsWithChildren, Suspense, lazy } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import AuthGuard from '../guards/AuthGuard';
import GuestGuard from '../guards/GuestGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// components
import MerchantOnlyLayout from '@/layouts/MerchantOnlyLayout';
import BookingOnboardingPage from '@/pages/public/booking/onboarding';
import OrderBuddyBookingPrivacyPolicy from '@/pages/public/booking/privacy/privacy';
import OrderBuddyBookingTermsAndConditions from '@/pages/public/booking/terms/terms';
import FAQPreview from '../components/_dashboard/FAQ/preview/preview';
import LoadingScreen from '../components/LoadingScreen';
import AppSections from '../pages/dashboard/appSections/AppSections';
import BookingDetail from '../pages/dashboard/booking/BookingDetail';
import CookingCategory from '../pages/dashboard/menu/menuCategory/CookingCategory';
import MerchantList from '../pages/dashboard/merchants/MerchantList';
import MerchantSettings from '../pages/dashboard/merchants/merchantSettings/MerchantSettings';
import PaymentError from '../pages/dashboard/merchants/subscriptions/payment/PaymentError';
import PaymentSuccess from '../pages/dashboard/merchants/subscriptions/payment/PaymentSuccess';
import ProductList from '../pages/dashboard/merchants/subscriptions/ProductList';
import Subscriptions from '../pages/dashboard/merchants/subscriptions/Subscriptions';
import PopularRestaurantCategories from '../pages/dashboard/PopularRestaurantCategories/PopularRestaurantCategories';
import BookingFormList from '../pages/dashboard/forms/BookingFormList';
import FormList from '../pages/dashboard/forms/FormList';
import { Promotion } from '../pages/dashboard/promotions/Promotion';
import PromotionHistory from '../pages/dashboard/promotions/PromotionHistory';
import FAQ from '../pages/public/FAQ/faq';
import GiftCardTerms from '../pages/public/giftCard/terms';
import MerchantGiftCardBalancePage from '../pages/public/merchantGiftCard/balance';
import MerchantGiftCardRedeemPage from '../pages/public/merchantGiftCard/redeem';
import PosInquiryPage from '../pages/public/pos/inquiry';

// ----------------------------------------------------------------------

type LoadableProps<P> = PropsWithChildren<P>;

const Loadable = <P extends object>(Component: ComponentType<P>): React.FC<LoadableProps<P>> => {
    const WrappedComponent: React.FC<LoadableProps<P>> = (props) => {
        const { pathname } = useLocation();
        const isDashboard = pathname.includes('/dashboard');

        return (
            <Suspense
                fallback={
                    <LoadingScreen
                        sx={{
                            ...(!isDashboard && {
                                top: 0,
                                left: 0,
                                width: 1,
                                zIndex: 9999,
                                position: 'fixed'
                            })
                        }}
                    />
                }
            >
                <Component {...(props as P)} />
            </Suspense>
        );
    };

    // Set display name for easier debugging
    WrappedComponent.displayName = `Loadable(${Component.displayName || Component.name || 'Component'})`;

    return WrappedComponent;
};

const Router: React.FC = () => {
    return useRoutes([
        {
            path: 'auth',
            children: [
                {
                    path: 'login',
                    element: (
                        <GuestGuard>
                            <Login />
                        </GuestGuard>
                    )
                },
                { path: 'login-unprotected', element: <Login /> },
                { path: 'reset-password', element: <ResetPassword /> },
                { path: 'verify', element: <VerifyCode /> }
            ]
        },
        {
            path: '/public',
            element: <LogoOnlyLayout />,
            children: [
                {
                    path: 'giftCard',
                    children: [{ path: 'terms', element: <GiftCardTerms /> }]
                },
                {
                    path: 'FAQ',
                    children: [{ path: ':app', element: <FAQ /> }]
                },
                {
                    path: 'pos',
                    children: [{ path: 'inquiry', element: <PosInquiryPage /> }]
                },
                {
                    path: 'booking',
                    children: [{ path: 'onboarding', element: <BookingOnboardingPage /> }]
                }
            ]
        },
        {
            path: '/public/booking',
            element: <MerchantOnlyLayout />,
            children: [
                {
                    path: ':merchantId/save',
                    element: <SaveCustomerBooking />
                },
                {
                    path: ':merchantId/query',
                    element: <QueryCustomerBooking />
                },
                {
                    path: ':merchantId/terms',
                    element: <OrderBuddyBookingTermsAndConditions />
                },
                {
                    path: ':merchantId/privacy',
                    element: <OrderBuddyBookingPrivacyPolicy />
                },
                {
                    path: ':merchantId/calendar',
                    element: <BookingCalendarPage />
                }
            ]
        },
        {
            path: '/public/merchantGiftCard',
            element: <MerchantOnlyLayout />,
            children: [
                {
                    path: ':merchantId/redeem',
                    element: <MerchantGiftCardRedeemPage />
                },
                {
                    path: ':merchantId/balance',
                    element: <MerchantGiftCardBalancePage />
                }
            ]
        },
        {
            path: '/public/promotion',
            children: [
                {
                    path: ':merchantId/appQRCode',
                    element: <PublicPromotionAppQRCode />
                }
            ]
        },

        // Dashboard Routes
        {
            path: '/dashboard',
            element: (
                <AuthGuard>
                    <DashboardLayout />
                </AuthGuard>
            ),
            children: [
                { element: <Navigate to="/dashboard/generalApp" replace /> },
                { path: '', element: <GeneralApp /> },
                { path: 'generalApp', element: <GeneralApp /> },
                {
                    path: 'user',
                    children: [
                        { element: <Navigate to="/dashboard/user/profile" replace /> },
                        { path: 'list', element: <UserList /> },
                        { path: 'new', element: <UserCreate /> },
                        { path: 'edit/:userId', element: <UserCreate /> },
                        { path: 'account', element: <UserAccount /> }
                    ]
                },
                {
                    path: 'promotion',
                    children: [
                        { element: <Navigate to="/dashboard/promotion/list" replace /> },
                        { path: 'list', element: <PromotionList /> },
                        {
                            path: 'appQRCode',
                            element: <PromotionAppQRCode />
                        },
                        { path: 'new', element: <PromotionCreate /> },
                        { path: 'send', element: <PromotionSendPage /> },
                        { path: 'send-history', element: <PromotionSendHistoryPage /> },
                        { path: 'history', element: <PromotionHistory /> },
                        { path: 'offer', element: <Promotion /> },
                        { path: 'offer/:name', element: <Promotion /> },
                        { path: 'loyalty/:promoType', element: <Promotion /> },
                        { path: 'loyalty/:promoType/:name', element: <Promotion /> },
                        { path: ':promoType', element: <Promotion /> },
                        { path: ':promoType/:name', element: <Promotion /> }
                    ]
                },
                {
                    path: 'loyalty',
                    children: [
                        { element: <Navigate to="/dashboard/loyalty/member" replace /> },
                        { path: 'member', element: <LoyaltyMemberPage /> },
                        { path: 'memberDetail', element: <LoyaltyMemberDetailPage /> }
                    ]
                },
                {
                    path: 'membership',
                    children: [
                        { element: <Navigate to="/dashboard/membership/members" replace /> },
                        { path: 'members', element: <MembershipMembersPage /> },
                        { path: 'memberDetail', element: <MembershipMemberDetailPage /> },
                        { path: 'plans', element: <MembershipPlansPage /> }
                    ]
                },
                {
                    path: 'giftcard',
                    children: [
                        { element: <Navigate to="/dashboard/giftCard/balance" replace /> },
                        { path: 'accounts', element: <GiftCardAccounts /> },
                        { path: 'balance/', element: <GiftCardBalance /> },
                        { path: 'history', element: <GiftCardHistory /> }
                    ]
                },
                {
                    path: 'merchantGiftCard',
                    children: [
                        { element: <Navigate to="/dashboard/merchantGiftCard/wallets" replace /> },
                        { path: 'wallets', element: <MerchantGiftCardWalletsPage /> },
                        { path: 'member', element: <MerchantGiftCardMemberPage /> },
                        { path: 'create', element: <MerchantGiftCardCreatePage /> },
                        { path: 'memberDetail', element: <MerchantGiftCardMemberDetailPage /> },
                        { path: 'topUp', element: <MerchantGiftCardTopUpPage /> }
                    ]
                },
                {
                    path: 'FAQ',
                    children: [
                        { element: <Navigate to="/FAQ/sections" replace /> },
                        { path: 'sections', element: <FAQSections /> },
                        { path: 'preview', element: <FAQPreview /> }
                    ]
                },
                {
                    path: 'appSections',
                    children: [
                        { element: <Navigate to="/dashboard/appSections/list" replace /> },
                        { path: 'list', element: <AppSections /> }
                    ]
                },
                {
                    path: 'popular-restaurant-categories',
                    children: [
                        { element: <Navigate to="/dashboard/popular-restaurant-categories/list" replace /> },
                        { path: 'list', element: <PopularRestaurantCategories /> }
                    ]
                },
                {
                    path: 'forms',
                    children: [
                        { element: <Navigate to="/dashboard/forms/pos" replace /> },
                        { path: 'list', element: <Navigate to="/dashboard/forms/pos" replace /> },
                        { path: 'pos', element: <FormList /> },
                        { path: 'booking', element: <BookingFormList /> }
                    ]
                },
                {
                    path: 'ticket',
                    children: [
                        { element: <Navigate to="/dashboard/ticket/view" replace /> },
                        { path: 'create', element: <TicketCreate /> },
                        { path: 'view', element: <TicketView /> }
                    ]
                },
                {
                    path: 'merchant',
                    children: [
                        { element: <Navigate to="/dashboard/merchant/list" replace /> },
                        { path: 'list', element: <MerchantList /> },
                        { path: 'new', element: <MerchantCreate /> },
                        { path: 'update-merchant', element: <MerchantUpdate /> },
                        { path: 'surcharges', element: <MerchantUpdateSurchargeList /> },
                        { path: 'updateOpeningHours', element: <MerchantOpentime /> },
                        { path: ':id/edit', element: <MerchantCreate /> },
                        { path: ':id/settings', element: <MerchantSettings /> },
                        { path: 'subscriptions', element: <Subscriptions /> },
                        { path: 'products', element: <ProductList /> }
                    ]
                },
                {
                    path: 'menu',
                    children: [
                        { element: <Navigate to="/dashboard/menu/list" replace /> },
                        { path: 'list', element: <MenuList /> },
                        { path: 'new', element: <MenuCreate /> },
                        { path: ':id/edit', element: <MenuCreate /> },
                        { path: ':id/menuCategory', element: <MenuCategory /> },
                        { path: 'menuGroup/:menuId', element: <MenuGroup /> },
                        { path: 'createMenuGroup/:menuId', element: <CreateMenuGroup /> },
                        { path: ':id/cookingCategory', element: <CookingCategory /> }
                    ]
                },
                {
                    path: 'order',
                    children: [
                        { element: <Navigate to="/dashboard/order/list" replace /> },
                        { path: 'list', element: <OrderList /> },
                        { path: ':orderNumber/detail', element: <OrderDetail /> }
                    ]
                },
                {
                    path: 'sessions-booking',
                    children: [
                        { element: <Navigate to="/dashboard/sessions-booking/list" replace /> },
                        { path: 'list', element: <BookingSessionsList /> },
                        { path: 'new', element: <BookingSessionsCreate /> },
                        { path: ':id/edit', element: <BookingSessionsCreate /> }
                    ]
                },
                {
                    path: 'booking',
                    children: [
                        { element: <Navigate to="/dashboard/booking/list" replace /> },
                        { path: 'settings', element: <BookingSettings /> },
                        { path: 'message', element: <BookingMessage /> },
                        { path: 'google', element: <BookingGoogleSettings /> },
                        { path: 'floormap', element: <BookingFloormap /> },
                        { path: 'updateBookingHours', element: <UpdateBookingHours /> },
                        { path: 'list', element: <BookingList /> },
                        { path: 'view', element: <BookingView /> },
                        { path: 'calendar', element: <BookingCalendar /> },
                        { path: 'tables', element: <BookingTables /> },
                        { path: 'report', element: <BookingReport /> },
                        { path: 'platformReport', element: <BookingPlatformReportView /> },
                        { path: 'smsReport', element: <SmsReportView /> },
                        { path: 'clients', element: <BookingClients /> },
                        { path: 'link', element: <BookingLink /> },
                        { path: 'create', element: <BookingCreate /> },
                        { path: ':id/detail', element: <BookingDetail /> }
                    ]
                },
                {
                    path: 'announcementBroadcast',
                    children: [
                        { element: <Navigate to="/dashboard/announcementBroadcast/list" replace /> },
                        { path: 'list', element: <AnnouncementBroadcastListPage /> },
                        { path: 'create', element: <AnnouncementBroadcastCreatePage /> },
                        { path: ':id/edit', element: <AnnouncementBroadcastCreatePage /> }
                    ]
                },
                {
                    path: 'managers',
                    children: [
                        { element: <Navigate to="/dashboard/managers/list" replace /> },
                        { path: 'list', element: <ManagersListPage /> },
                        { path: 'create', element: <ManagersCreatePage /> }
                    ]
                },
                {
                    path: 'bookingSubscription',
                    children: [
                        { element: <Navigate to="/dashboard/bookingSubscription/packages" replace /> },
                        { path: 'packages', element: <SubscriptionPackagesListPage /> },
                        { path: 'packages/create', element: <SubscriptionPackageCreatePage /> },
                        { path: 'packages/:id/edit', element: <SubscriptionPackageCreatePage /> },
                        { path: 'merchants', element: <MerchantSubscriptionListPage /> },
                        { path: 'merchants/:merchantId/adjust', element: <MerchantSubscriptionAdjustPage /> },
                        { path: 'mySubscription', element: <MySubscriptionPage /> }
                    ]
                },
                {
                    path: 'system',
                    children: [
                        { element: <Navigate to="/dashboard/system/apiHealth" replace /> },
                        { path: 'apiHealth', element: <APIHealth /> },
                        { path: 'shortUrl', element: <ShortUrl /> }
                    ]
                }
            ]
        },
        {
            path: 'subscriptions',
            element: <LogoOnlyLayout />,
            children: [
                {
                    path: ':id/payment',
                    children: [
                        { path: 'success', element: <PaymentSuccess /> },
                        { path: 'error', element: <PaymentError /> }
                    ]
                }
            ]
        },
        // Main Routes
        {
            path: '*',
            element: <LogoOnlyLayout />,
            children: [
                { path: 'coming-soon', element: <ComingSoon /> },
                { path: 'maintenance', element: <Maintenance /> },
                { path: 'pricing', element: <Pricing /> },
                { path: 'payment', element: <Payment /> },
                { path: '500', element: <Page500 /> },
                { path: '404', element: <NotFound /> },
                { path: '*', element: <Navigate to="/404" replace /> }
            ]
        },
        {
            path: '/',
            element: <LandingPage />
        },
        { path: '*', element: <Navigate to="/404" replace /> }
    ]);
};

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/authentication/VerifyCode')));
// Dashboard
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));
// Main
const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
// const Promotions = Loadable(lazy(() => import('../pages/dashboard/Promotions')));
const PromotionList = Loadable(lazy(() => import('../pages/dashboard/promotions/PromotionList')));
const PromotionAppQRCode = Loadable(lazy(() => import('../pages/dashboard/promotions/PromotionAppQRCode')));
const PromotionCreate = Loadable(lazy(() => import('../pages/dashboard/promotions/PromotionCreate')));
const PromotionSendPage = Loadable(lazy(() => import('../pages/dashboard/promotions/PromotionSend')));
const PromotionSendHistoryPage = Loadable(lazy(() => import('../pages/dashboard/promotions/PromotionSendHistory')));
const LoyaltyMemberPage = Loadable(lazy(() => import('../pages/dashboard/loyalty/member/LoyaltyMembers')));
const LoyaltyMemberDetailPage = Loadable(lazy(() => import('../pages/dashboard/loyalty/member/LoyaltyMemberDetail')));
const MembershipMembersPage = Loadable(lazy(() => import('../pages/dashboard/membership/MembershipMembers')));
const MembershipMemberDetailPage = Loadable(lazy(() => import('../pages/dashboard/membership/MembershipMemberDetail')));
const MembershipPlansPage = Loadable(lazy(() => import('../pages/dashboard/membership/MembershipPlans')));
const MerchantGiftCardWalletsPage = Loadable(lazy(() => import('../pages/dashboard/merchantGiftCard/wallets/MerchantGiftCardWallets')));
const MerchantGiftCardMemberPage = Loadable(lazy(() => import('../pages/dashboard/merchantGiftCard/member/MerchantGiftCardMember')));
const MerchantGiftCardCreatePage = Loadable(lazy(() => import('../pages/dashboard/merchantGiftCard/create/MerchantGiftCardCreate')));
const MerchantGiftCardMemberDetailPage = Loadable(
    lazy(() => import('../pages/dashboard/merchantGiftCard/memberDetail/MerchantGiftCardMemberDetail'))
);
const MerchantGiftCardTopUpPage = Loadable(lazy(() => import('../pages/dashboard/merchantGiftCard/topUp/MerchantGiftCardTopUp')));
const GiftCardBalance = Loadable(lazy(() => import('../pages/dashboard/giftCard/balance')));
const GiftCardAccounts = Loadable(lazy(() => import('../pages/dashboard/giftCard/accounts')));
const GiftCardHistory = Loadable(lazy(() => import('../pages/dashboard/giftCard/history')));
// FAQ
const FAQSections = Loadable(lazy(() => import('../pages/dashboard/FAQ/sections')));
const TicketCreate = Loadable(lazy(() => import('../pages/dashboard/ticket/TicketCreate')));
const TicketView = Loadable(lazy(() => import('../pages/dashboard/ticket/TicketView')));
// const MerchantList = Loadable(lazy(() => import('../pages/dashboard/merchants/MerchantList')));
const MerchantCreate = Loadable(lazy(() => import('../pages/dashboard/merchants/MerchantCreate')));
const MerchantUpdate = Loadable(lazy(() => import('../pages/dashboard/merchants/MerchantUpdate')));
const MerchantUpdateSurchargeList = Loadable(lazy(() => import('../pages/dashboard/merchants/surcharges/MerchantUpdateSurchargeList')));
const MerchantOpentime = Loadable(lazy(() => import('../pages/dashboard/merchants/MerchantOpentime')));

const MenuList = Loadable(lazy(() => import('../pages/dashboard/menu/MenuList')));
const MenuCreate = Loadable(lazy(() => import('../pages/dashboard/menu/MenuCreate')));
const MenuGroup = Loadable(lazy(() => import('../pages/dashboard/menu/MenuGroup')));
const CreateMenuGroup = Loadable(lazy(() => import('../pages/dashboard/menu/CreateMenuGroup')));

const OrderList = Loadable(lazy(() => import('../pages/dashboard/order/OrderList')));
const OrderDetail = Loadable(lazy(() => import('../pages/dashboard/order/OrderDetail')));

const APIHealth = Loadable(lazy(() => import('../pages/dashboard/system/apiHealth')));
const ShortUrl = Loadable(lazy(() => import('../pages/dashboard/system/shortUrl')));

const BookingList = Loadable(lazy(() => import('../pages/dashboard/booking/BookingList')));
const BookingView = Loadable(lazy(() => import('../pages/dashboard/booking/BookingView')));
const BookingCalendar = Loadable(lazy(() => import('../pages/dashboard/booking/BookingCalendar')));
const BookingReport = Loadable(lazy(() => import('../pages/dashboard/booking/BookingReport')));
const BookingPlatformReportView = Loadable(lazy(() => import('../pages/dashboard/booking/BookingPlatformReport')));
const SmsReportView = Loadable(lazy(() => import('../pages/dashboard/booking/SmsReport')));
const BookingTables = Loadable(lazy(() => import('../pages/dashboard/booking/BookingTables')));
const BookingSettings = Loadable(lazy(() => import('../pages/dashboard/booking/BookingSettings')));
const BookingMessage = Loadable(lazy(() => import('../pages/dashboard/booking/BookingMessage')));
const BookingGoogleSettings = Loadable(lazy(() => import('../pages/dashboard/booking/BookingGoogleIntegration')));
const BookingFloormap = Loadable(lazy(() => import('../pages/dashboard/booking/BookingFloormap')));
const UpdateBookingHours = Loadable(lazy(() => import('../pages/dashboard/booking/UpdateBookingHours')));
const BookingClients = Loadable(lazy(() => import('../pages/dashboard/booking/BookingClients')));
const BookingCreate = Loadable(lazy(() => import('../pages/dashboard/booking/BookingCreate')));
const BookingLink = Loadable(lazy(() => import('../pages/dashboard/booking/BookingLink')));
const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));

const BookingSessionsList = Loadable(lazy(() => import('../pages/dashboard/BookingSessionsList')));
const BookingSessionsCreate = Loadable(lazy(() => import('../pages/dashboard/BookingSessionsCreate')));

const MenuCategory = Loadable(lazy(() => import('../pages/dashboard/menu/menuCategory/MenuCategory')));

const SaveCustomerBooking = Loadable(lazy(() => import('../pages/public/booking/save/saveBooking')));
const QueryCustomerBooking = Loadable(lazy(() => import('../pages/public/booking/query/queryBooking')));
const PublicPromotionAppQRCode = Loadable(lazy(() => import('../pages/public/promotion/AppQRCode')));
const BookingCalendarPage = Loadable(lazy(() => import('../pages/public/booking/calendar/calendar')));

const ManagersListPage = Loadable(lazy(() => import('../pages/dashboard/managers/managersList')));
const ManagersCreatePage = Loadable(lazy(() => import('../pages/dashboard/managers/managersCreate')));

const AnnouncementBroadcastListPage = Loadable(lazy(() => import('../pages/dashboard/announcementBroadcast/announcementBroadcastList')));
const AnnouncementBroadcastCreatePage = Loadable(
    lazy(() => import('../pages/dashboard/announcementBroadcast/announcementBroadcastCreate'))
);

const SubscriptionPackagesListPage = Loadable(
    lazy(() => import('../pages/dashboard/subscription/subscriptionPackagesList'))
);
const SubscriptionPackageCreatePage = Loadable(
    lazy(() => import('../pages/dashboard/subscription/subscriptionPackageCreate'))
);
const MerchantSubscriptionListPage = Loadable(
    lazy(() => import('../pages/dashboard/subscription/merchantSubscriptionList'))
);
const MerchantSubscriptionAdjustPage = Loadable(
    lazy(() => import('../pages/dashboard/subscription/merchantSubscriptionAdjust'))
);
const MySubscriptionPage = Loadable(
    lazy(() => import('../pages/dashboard/subscription/mySubscription'))
);

export default Router;
